import React, { useEffect, useState } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import appIcon from '../assets/logo-dark.svg';

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="#9333ea" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const DotsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#9333ea">
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
  </svg>
);

function isChromeiOS() { return /crios/i.test(navigator.userAgent); }
function isFirefoxiOS() { return /fxios/i.test(navigator.userAgent); }

export const PWAInstallBanner: React.FC = () => {
  const { installState, triggerInstall, dismiss } = usePWAInstall();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (installState === 'android-ready' || installState === 'ios-manual') {
      const t = setTimeout(() => setVisible(true), 1400);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [installState]);

  if (!visible) return null;

  const base: React.CSSProperties = {
    position: 'fixed',
    bottom: 24,
    left: 16,
    right: 16,
    zIndex: 300,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.94)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: '0.5px solid #e2e8f0',
    boxShadow: '0 4px 24px rgba(147,51,234,0.16), 0 2px 8px rgba(0,0,0,0.08)',
    animation: 'pwaSlideUp 0.3s ease-out both',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  };

  if (installState === 'android-ready') return (
    <div style={base}>
      <img src={appIcon} width={40} height={40} alt="Attendance" style={{ flexShrink: 0, borderRadius: 10 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.title}>Install Attendance App</div>
        <div style={s.sub}>Add to home screen for quick access</div>
      </div>
      <button style={s.skip} onClick={dismiss}>Skip</button>
      <button style={s.install} onClick={async () => { const ok = await triggerInstall(); if (!ok) dismiss(); }}>
        Install
      </button>
    </div>
  );

  if (installState === 'ios-manual') {
    const chromeIOS  = isChromeiOS();
    const firefoxIOS = isFirefoxiOS();

    const instructions = chromeIOS ? (
      <div style={s.strip}>
        <span>Tap</span><DotsIcon /><span>then</span>
        <span style={{ color: '#9333ea', fontWeight: 600 }}>"Add to Home Screen"</span>
      </div>
    ) : firefoxIOS ? (
      <div style={s.strip}>
        <span>Tap the</span>
        <span style={{ color: '#9333ea', fontWeight: 600 }}>address bar menu</span>
        <span>then</span>
        <span style={{ color: '#9333ea', fontWeight: 600 }}>"Add to Shortcuts"</span>
      </div>
    ) : (
      <div style={s.strip}>
        <span>Tap</span><ShareIcon /><span>then</span>
        <span style={{ color: '#9333ea', fontWeight: 600 }}>"Add to Home Screen"</span>
      </div>
    );

    return (
      <div style={{ ...base, flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={appIcon} width={40} height={40} alt="Attendance" style={{ flexShrink: 0, borderRadius: 10 }} />
          <div style={{ flex: 1 }}>
            <div style={s.title}>Install Attendance App</div>
            <div style={s.sub}>Add to home screen</div>
          </div>
          <button style={s.skip} onClick={dismiss}>✕</button>
        </div>
        {instructions}
      </div>
    );
  }

  return null;
};

const s: Record<string, React.CSSProperties> = {
  title:   { fontSize: 13, fontWeight: 700, color: '#06050D', lineHeight: 1.3, letterSpacing: '-0.01em' },
  sub:     { fontSize: 11, color: '#64748b', lineHeight: 1.3, marginTop: 2 },
  skip:    { background: 'transparent', border: 'none', fontSize: 12, color: '#94a3b8', cursor: 'pointer', padding: '4px 6px', fontFamily: 'inherit' },
  install: { background: 'transparent', border: 'none', fontSize: 12, fontWeight: 700, color: '#9333ea', cursor: 'pointer', padding: '4px 6px', fontFamily: 'inherit' },
  strip:   { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', background: '#f3e8ff', borderRadius: 8, padding: '8px 12px', flexWrap: 'wrap' },
};

export default PWAInstallBanner;
